import { FilterDateSelectValue } from '../../../core/entities/filterDateSelect/filter-date-select-value';
import { StateValidator } from '../../../core/services/filter-state/filter-state.service';

/**
 * Interface representing the persisted filter state for the My Tasks List.
 * This is what gets saved to and restored from localStorage.
 * 
 * Note: We store primitive/serializable values (codes, IDs) rather than
 * full objects to ensure proper JSON serialization and avoid stale data.
 * 
 * Differences from TaskListFilterState:
 * - No tabIndex (no tabs in My Tasks)
 * - No stateCodes (fixed to ASSIGNED)
 * - No completedDate (only assigned tasks are shown)
 */
export interface MyTaskListFilterState {
  /** Task type codes */
  typeCodes: string[];

  /** Location IDs */
  locationIds: string[];

  /** Due date filter configuration */
  dueDate: FilterDateSelectValue | null;

  /** Sort configuration */
  sort: {
    key: string;
    direction?: 'asc' | 'desc';
  };

  /** Page size preference */
  pageSize: number;

  /** Displayed field identifiers (optional for backward compatibility) */
  displayedFields?: string[];

  /** Show metadata row toggle */
  showMetadata?: boolean;
}

/**
 * Type guard to validate restored filter state.
 * Returns false if the structure doesn't match, causing the service
 * to clear the stored state and return null.
 * 
 * This protects against schema changes - if we add/remove/rename fields,
 * old stored data will be safely discarded.
 */
export const isValidMyTaskListFilterState: StateValidator<MyTaskListFilterState> = (
  obj: unknown
): obj is MyTaskListFilterState => {
  // Must be an object
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const state = obj as Record<string, unknown>;

  // Validate required fields and their types
  if (!Array.isArray(state.typeCodes)) return false;
  if (!Array.isArray(state.locationIds)) return false;
  if (typeof state.pageSize !== 'number') return false;

  // dueDate can be null or an object
  if (state.dueDate !== null && typeof state.dueDate !== 'object') return false;

  // sort must be an object with a key property
  if (!state.sort || typeof state.sort !== 'object') return false;
  const sort = state.sort as Record<string, unknown>;
  if (typeof sort.key !== 'string') return false;

  // displayedFields is optional but must be an array if present
  if (state.displayedFields !== undefined && !Array.isArray(state.displayedFields)) return false;

  // showMetadata is optional but must be a boolean if present
  if (state.showMetadata !== undefined && typeof state.showMetadata !== 'boolean') return false;

  return true;
};
